import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import DocumentHorizontal20 from '@carbon/icons-react/es/document--horizontal/20';
import DocumentVertical20 from '@carbon/icons-react/es/document--vertical/20';
import { Dropdown, Space } from 'antd';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { Switcher } from 'components/atoms/switcher';
import { PermissionRuleName } from 'constant';
import { ProjectPlanFilter, ProjectPlanFilterOption } from 'data/projects';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslation } from 'hooks/use-translation';

const FormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    font-size: 14px;
    margin-right: 8px;
    margin-top: -2px; /* this is to fix antd form label to work with radio with icons. Open to suggestions 1*/
  }
`;

const IconWrapper = styled.i`
  vertical-align: middle;
  height: 16px;
  display: inline-block;
`;
const MenuItem = styled(Menu.Item)`
  padding: var(--spacing-03) var(--spacing-05) !important;
`;

const ResponsibleSelectTrigger = styled(Button)`
  padding-top: 0;
  padding-bottom: 0;
  margin-top: calc(-1 * calc(0.375rem - 3px));
`;

export interface PrintSettings {
  status: string;
  responsible: string | number;
  exportFormat: string;
  orientation: string;
  showPrivateTasks: boolean;
}

interface DataProps {
  statusFilters: ProjectPlanFilterOption[];
  responsibleFilters: ProjectPlanFilterOption[];
  selected: ProjectPlanFilter;
}

interface ExportDialog extends DataProps {
  title?: string | React.ReactNode;
  isLoading?: boolean;
  visible?: boolean;
  onCancel?: (val?: any) => void;
  onOkay: (formValues) => void;
  onChange: (val: PrintSettings) => void;
  formats?: string[];
  defaultFormat?: string;
  showPrivateTasks?: boolean;
}

const ResponsibleSelect: FC<{
  options: ProjectPlanFilterOption[];
  value?: number;
  onChange?: (value?: number) => void;
}> = ({ options, value, onChange }) => {
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  return (
    <Dropdown
      overlay={
        <Menu selectedKeys={[selected.value.toString()]}>
          {options.map((option) => (
            <MenuItem
              key={option.value.toString()}
              icon={<Avatar name={option.label} src={option.companyLogoUrl} />}
              onClick={() => onChange?.(option.value as number)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      }
    >
      <ResponsibleSelectTrigger
        type="text"
        size="small"
        icon={<ChevronDown16 />}
        iconPlacement="right"
      >
        <Space size={8}>
          <Avatar name={selected.label} src={selected.companyLogoUrl} />
          {selected.label}
        </Space>
      </ResponsibleSelectTrigger>
    </Dropdown>
  );
};

export const ExportDialog: FC<ExportDialog> = ({
  title,
  isLoading = false,
  visible = false,
  onCancel,
  onOkay,
  statusFilters,
  responsibleFilters,
  selected,
  onChange,
  formats,
  defaultFormat,
  showPrivateTasks = true,
}) => {
  const status = useTranslation('status');
  const responsible = useTranslation('responsible');
  const defaultTitle = useTranslation('plan.export.title');
  const exportButton = useTranslation('export');
  const privateInputLabel = useTranslation('plan.export.private.tasks');
  const orientationInputLabel = useTranslation('plan.export.orientation');
  const formatInputLabel = useTranslation('plan.export.format');
  const yesText = useTranslation('yes');
  const noText = useTranslation('no');

  const _defaultFormats = ['pdf'];
  const orientationSupportedFormats = ['pdf'];
  const _formats = formats ?? _defaultFormats;

  const [form] = Form.useForm<PrintSettings>();
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasAccessToExportPrivateResource = isAllowedForCurrentUser(
    PermissionRuleName.ProjectExportPrivateResourceAction,
  );

  const initialValues = useMemo(
    () => ({
      status: String(selected.primary.value),
      responsible: selected.secondary.value,
      exportFormat: defaultFormat || _formats[0],
      orientation: 'portrait',
      showPrivateTasks: hasAccessToExportPrivateResource
        ? showPrivateTasks
        : false,
    }),
    [
      _formats,
      defaultFormat,
      hasAccessToExportPrivateResource,
      selected.primary.value,
      selected.secondary.value,
      showPrivateTasks,
    ],
  );

  const onSubmit = useCallback(async () => {
    const formValues = await form.validateFields();

    onOkay(formValues);
  }, [form, onOkay]);

  useEffect(() => {
    if (visible && !hasAccessToExportPrivateResource) {
      onChange({
        ...initialValues,
        showPrivateTasks: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccessToExportPrivateResource, visible]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      onOk={onSubmit}
      title={title ?? defaultTitle}
      okText={exportButton}
      width={612}
      maskClosable={false}
      destroyOnClose
      confirmLoading={isLoading}
    >
      <Form
        form={form}
        initialValues={initialValues}
        name="export-pdf-form"
        layout="horizontal"
        colon={false}
        labelCol={{ span: 7 }}
        onValuesChange={(_, allValues: any) => onChange(allValues)}
        style={{ padding: '30px 0' }}
      >
        {statusFilters ? (
          <FormItem name="status" label={status}>
            <Switcher>
              {statusFilters.map((filter) => (
                <Switcher.Button key={filter.id} value={filter.value}>
                  {filter.label}
                </Switcher.Button>
              ))}
            </Switcher>
          </FormItem>
        ) : null}

        {responsibleFilters ? (
          <FormItem name="responsible" label={responsible}>
            <ResponsibleSelect options={responsibleFilters} />
          </FormItem>
        ) : null}

        <FormItem name="exportFormat" label={formatInputLabel}>
          <Radio.Group>
            {_formats.map((format) => (
              <Radio key={format} value={format}>
                <FormattedMessage id={format} />
              </Radio>
            ))}
          </Radio.Group>
        </FormItem>

        <Form.Item
          noStyle
          shouldUpdate={(prev, current) =>
            prev.exportFormat !== current.exportFormat
          }
        >
          {({ getFieldValue }) =>
            orientationSupportedFormats.includes(
              getFieldValue('exportFormat'),
            ) ? (
              <FormItem name="orientation" label={orientationInputLabel}>
                <Radio.Group>
                  <Radio value="portrait">
                    <IconWrapper>
                      <DocumentVertical20 />
                    </IconWrapper>
                  </Radio>
                  <Radio value="landscape">
                    <IconWrapper>
                      <DocumentHorizontal20 />
                    </IconWrapper>
                  </Radio>
                </Radio.Group>
              </FormItem>
            ) : null
          }
        </Form.Item>

        <FormItem
          name="showPrivateTasks"
          label={privateInputLabel}
          hidden={!hasAccessToExportPrivateResource}
        >
          <Radio.Group>
            <Radio value>{yesText}</Radio>
            <Radio value={false}>{noText}</Radio>
          </Radio.Group>
        </FormItem>
      </Form>
    </Modal>
  );
};
